/**
 * The live accounts, and who owes the next move.
 *
 * WHAT THIS PANEL IS FOR. Every other surface on the deck answers "what is outstanding" from
 * the vault. This one answers "where does the money actually stand", read from Twenty, which is
 * the system of record for it. The server built the rows (see `mo/pipelinePanel.ts`); this
 * renders them and adds nothing.
 *
 * THE THREE RULES IT EXISTS TO KEEP, each of which was paid for:
 *
 *  1. **Absent is not empty.** `pipeline` is optional on the payload precisely so a server with
 *     no CRM credentials, or one whose Twenty call failed, omits it. A Pipeline panel showing an
 *     empty list is indistinguishable from "you have no live accounts", so this component
 *     renders NOTHING at all when the field is absent rather than an empty state.
 *  2. **Stage is what Twenty says.** `proposedStage` renders as a question with its reasoning,
 *     never merged into the stage badge. Rendering a proposal as though it were the state is the
 *     same fabrication the rest of the deck refuses.
 *  3. **`daysQuiet: null` is not zero.** An account with no mail at all (Barter has never sent
 *     one) has no days-quiet figure, and rendering 0 would read as "spoke today". It shows as
 *     "no mail" and direction carries the meaning instead.
 *
 * Direction leads each row because it is the field that makes a date actionable: "14 days quiet"
 * is identical for an account ignoring him and one he has not replied to, and the two call for
 * opposite actions.
 */

import type { JSX } from 'react';
import type { DashboardChase, DashboardPipelineAccount, PipelineDirection } from '@rbv/shared';
import { ChaseRow } from './Chase.js';
import { Icon } from './Icon.js';

/** His words for each state, not the enum. `unknown` is shown, never hidden as a blank. */
const DIRECTION_LABEL: Record<PipelineDirection, string> = {
  'they-owe': 'they owe you',
  'you-owe': 'you owe them',
  'meetings-only': 'met in person',
  unknown: 'direction not known',
};

/**
 * Quiet time, or an honest refusal.
 *
 * Null means the account has never exchanged mail, which is a different fact from "quiet for
 * zero days" and must not render as a number.
 */
function quietLabel(days: number | null, direction: PipelineDirection): string {
  if (days === null) return 'no mail on record';
  if (direction === 'meetings-only') return `${days}d since mail, seen in person`;
  if (days === 0) return 'today';
  return `${days}d quiet`;
}

function dueLabel(iso: string | null): string | null {
  if (iso === null) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const days = Math.round((t - Date.now()) / 86_400_000);
  if (days < 0) return `follow-up ${Math.abs(days)}d overdue`;
  if (days === 0) return 'follow-up today';
  return `follow-up in ${days}d`;
}

export function PipelinePanel({
  accounts,
  chases,
  onReload,
}: {
  /** Undefined when the server could not read Twenty. See rule 1 above. */
  accounts: DashboardPipelineAccount[] | undefined;
  /**
   * The chase queue, full, above the accounts.
   *
   * It lives HERE rather than on either dashboard shape because this component is rendered by
   * both, so putting the queue where he already looks at accounts is also what stops the deck
   * and the tower disagreeing about who is owed a move.
   *
   * Undefined follows rule 1 exactly as `accounts` does: absent means the CRM did not answer,
   * and it must not render as an empty group.
   */
  chases?: DashboardChase[];
  /**
   * Called after a snooze or a drop lands, so the payload is refetched.
   *
   * Required, not optional with a no-op default: a defaulted callback here would leave a
   * snoozed account still on screen and read as the button having done nothing.
   */
  onReload: () => void;
}): JSX.Element | null {
  if (accounts === undefined) return null;

  /*
   * Present but empty is a real answer and a different one from absent: the server reached
   * Twenty and found no live accounts. It says so rather than rendering a bare panel.
   */
  if (accounts.length === 0) {
    return (
      <section className="deck-panel" data-hue="waiting">
        <header className="deck-panel-head">
          <Icon name="waiting" size={16} className="deck-panel-icon" />
          <h2 className="deck-panel-title">Accounts</h2>
        </header>
        <div className="deck-panel-body">
          <p className="deck-panel-note">Twenty answered, and no account is currently live.</p>
        </div>
      </section>
    );
  }

  /*
   * Ordered by who is waiting on HIM. Sorting by days quiet would put the accounts that need
   * nothing from him at the top, which is the opposite of what this panel is for.
   */
  const rank: Record<PipelineDirection, number> = {
    'you-owe': 0,
    'they-owe': 1,
    unknown: 2,
    'meetings-only': 3,
  };
  const rows = [...accounts].sort(
    (a, b) => rank[a.direction] - rank[b.direction] || (b.daysQuiet ?? -1) - (a.daysQuiet ?? -1),
  );
  const youOwe = rows.filter((a) => a.direction === 'you-owe').length;
  /*
   * WITHHELD IS NOT UNKNOWN, which is the same distinction the three rules above keep.
   *
   * The server strips direction, verdict and stage proposals from these rows when the lane that
   * draws them is quarantined (see withholdConclusions in mo/queries.ts). Rendered without a word,
   * that is indistinguishable from an account nothing is known about, and the summary line below
   * would go further and read "Nobody is waiting on you", which is a conclusion drawn from the
   * absence of the very readings that were withheld.
   */
  const withheld = rows.some((a) => a.conclusionsWithheld === true);

  return (
    <section className="deck-panel" data-hue="waiting">
      <header className="deck-panel-head">
        <Icon name="waiting" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">Accounts</h2>
        <span className="deck-count">{rows.length}</span>
      </header>
      <div className="deck-panel-body">
        {/*
          ABOVE the accounts, because the queue is the actionable subset of the list under it and
          a queue below its own source is a footnote. Absent renders nothing at all (rule 1); an
          empty array renders nothing either, because "0 to chase" is a count nobody needs.
        */}
        {chases !== undefined && chases.length > 0 && (
          <div className="mo-chase-group">
            <p className="mo-chase-head">
              Needs a chase
              <span>{chases.length}</span>
            </p>
            <ul className="mo-chase-list">
              {chases.map((c) => (
                <ChaseRow key={c.account} chase={c} onDone={onReload} />
              ))}
            </ul>
          </div>
        )}
        <p className="deck-panel-note">
          {withheld
            ? 'Readings withheld: the CRM lane is down, so who owes the next move, the verdicts and any stage proposals are not being shown. The dates and contacts below are the last that were observed.'
            : youOwe > 0
              ? `${youOwe} of ${rows.length} are waiting on you. Stage is what Twenty holds.`
              : `Nobody is waiting on you. Stage is what Twenty holds.`}
        </p>
        <ul className="mo-pipeline">
          {rows.map((a) => {
            const due = dueLabel(a.followUpDueAt);
            return (
              <li key={a.account} className="mo-pipeline-row" data-direction={a.direction}>
                <div className="mo-pipeline-main">
                  <a className="mo-pipeline-name" href={a.twentyUrl} target="_blank" rel="noreferrer">
                    {a.account}
                  </a>
                  <span className="mo-pipeline-stage">{a.stage}</span>
                </div>
                <div className="mo-pipeline-meta">
                  <span className="mo-pipeline-direction">
                    {a.conclusionsWithheld === true
                      ? 'direction withheld'
                      : DIRECTION_LABEL[a.direction]}
                  </span>
                  <span className="mo-pipeline-quiet">{quietLabel(a.daysQuiet, a.direction)}</span>
                  {/* Empty when nobody there has ever written, rather than a guessed name. */}
                  {a.pointOfContact !== '' && (
                    <span className="mo-pipeline-poc">{a.pointOfContact}</span>
                  )}
                  {due !== null && <span className="mo-pipeline-due">{due}</span>}
                </div>
                {/*
                  WHAT THE NUMBERS MEAN, under the numbers.
                  
                  His complaint on 2026-08-11 was that the board was "simple read out no insight":
                  it printed stage, direction, days quiet and a contact and never said the obvious
                  thing, which for AMEK was that four unanswered messages over 258 days is not a
                  stalled deal but a lead that never started.
                  
                  Deterministic, from mo/accountVerdict.ts, and it NAMES ITS OWN EVIDENCE, so the
                  reading sits beside the facts it rests on rather than replacing them.
                */}
                {a.verdict !== undefined && (
                  <p className="mo-pipeline-verdict" data-tone={a.verdict.tone}>
                    {a.verdict.line}
                  </p>
                )}
                {a.contactRisk !== undefined && (
                  <p className="mo-pipeline-risk">{a.contactRisk}</p>
                )}
                {/* A question, never a fact. See rule 2. */}
                {a.proposedStage !== null && (
                  <p className="mo-pipeline-proposal">
                    Move to <strong>{a.proposedStage}</strong>? {a.proposalWhy}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
